import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { normalizeAngle } from '@/editor/math/utils';
import { EDoorWindowSide, IWindowData } from '@/editor/data/model/model';
import { globalStores } from '@/editor/stores';
import { EEntityType } from '@/editor/types';
import { WallData } from '@/editor/data/wall/Wall';
import { SeparateWallData } from '@/editor/data/separateWall/SeparateWall';
import { cloneDeep } from 'lodash-es';
import { IShapeSeg2d } from '@/editor/types/shape';
import { LineSeg2d } from '@/editor/math/LineSeg2d';
import { ArcSeg2d } from '@/editor/math/arcSeg2d';
import { getPointFromRatio } from '@/editor/math/shape/other';
import { isArc } from '../arc';

export function getWindowCenter(seg: IShapeSeg2d, distance: number) {
  const foot = getPointFromRatio(seg, distance / seg.length);
  return foot;
}

// 计算内嵌构件朝向，默认朝向右侧
export function getWindowRotation(wallSeg: IShapeSeg2d, center: IPoint2d, isRightSide = true): number {
  let angle = 0;

  if (wallSeg instanceof LineSeg2d) {
    angle = wallSeg.getAngleWithXAxis();
  } else if (wallSeg instanceof ArcSeg2d) {
    angle = wallSeg.getTangentAngle(center);
  }

  return normalizeAngle(angle + (isRightSide ? Math.PI : 0));
}

export function getWindowThickness(doorWindowData: IWindowData) {
  let thickness = 0;
  if (doorWindowData.relativeWallInfo.wallType === EEntityType.SeparateWall) {
    const separateWallData = globalStores.separateWallStore.getSepData(
      doorWindowData.relativeWallInfo.relativeWallId,
      false
    );
    if (separateWallData) {
      thickness = separateWallData.thickness;
    }
  } else {
    const wallData = globalStores.wallStore.getWallData(doorWindowData.relativeWallInfo.relativeWallId);
    if (wallData) {
      thickness = wallData.baseData.thickness;
    }
  }

  return thickness;
}

export function getWindowDistance(doorWindowData: IWindowData) {
  let distance = doorWindowData.distance;
  if (doorWindowData.relativeWallInfo.wallType === EEntityType.SeparateWall) {
    const separateWallData = globalStores.separateWallStore.getSepData(
      doorWindowData.relativeWallInfo.relativeWallId,
      false
    );
    if (separateWallData) {
      distance = Point2dUtils.distanceTo(doorWindowData.center, separateWallData.start);
    }
  } else {
    const wallData = globalStores.wallStore.getWallData(doorWindowData.relativeWallInfo.relativeWallId);
    if (wallData) {
      distance = Point2dUtils.distanceTo(doorWindowData.center, wallData.baseData.start);
    }
  }

  return distance;
}

export function getWindowDirectedDistance(seg: IShapeSeg2d, windowCenter: IPoint2d) {
  if (seg instanceof LineSeg2d) {
    const _distance = Point2dUtils.distanceTo(windowCenter, seg.start);
    const wallDir = Point2dUtils.getDirection(seg.start, seg.end);
    const doorWindDir = Point2dUtils.getDirection(seg.start, windowCenter);

    const dot = doorWindDir.x * wallDir.x + doorWindDir.y * wallDir.y;

    return dot > 0 ? _distance : -_distance;
  } else if (seg instanceof ArcSeg2d) {
    const footRatio = seg.getFootRatio(windowCenter);
    const length = seg.length;

    return footRatio * length;
  }

  return 0;
}

// 由center、rotation重新计算distance、side，并更新thickness
export function syncCenterTypeToDistanceType(extendedWindowData: IWindowData) {
  const relativeWallInfo = extendedWindowData.relativeWallInfo;
  const entityData = globalStores.getDataByIdType(
    relativeWallInfo.relativeWallId,
    relativeWallInfo.wallType || EEntityType.Wall
  );

  // 关联墙丢失，返回原数据
  const newData = cloneDeep(extendedWindowData);
  if (!entityData) return newData;

  let wallStart;
  let wallEnd;
  let wallSeg;

  if (relativeWallInfo.wallType === EEntityType.Wall) {
    const wallData = entityData as WallData;
    wallStart = wallData.baseData.start;
    wallEnd = wallData.baseData.end;
    // wallThiness = wallData.baseData.thickness;
    wallSeg = isArc(wallData.baseData)
      ? new ArcSeg2d({
          start: wallStart,
          end: wallEnd,
          center: wallData.baseData.center,
          clockwise: wallData.baseData.clockwise
        })
      : new LineSeg2d(wallStart, wallEnd);
  } else {
    const separateWallData = entityData as SeparateWallData;
    wallStart = separateWallData.start;
    wallEnd = separateWallData.end;
    // wallThiness = separateWallData.thickness;
    wallSeg = isArc(separateWallData)
      ? new ArcSeg2d({
          start: wallStart,
          end: wallEnd,
          center: separateWallData.center,
          clockwise: separateWallData.clockwise
        })
      : new LineSeg2d(wallStart, wallEnd);
  }

  const distance = getWindowDirectedDistance(wallSeg, extendedWindowData.center);
  const wallAngle = Point2dUtils.getAngle(wallStart, wallEnd);
  const windowAngle = normalizeAngle(extendedWindowData.rotation + Math.PI / 2);
  const side = Math.abs(windowAngle - wallAngle - Math.PI / 2) < 0.01 ? EDoorWindowSide.left : EDoorWindowSide.right;

  return { ...newData, distance, side };
}

// 由distance、side重新计算center、rotation，并更新thickness
export function syncDistanceTypeToCenterType(extendedWindowData: IWindowData) {
  const relativeWallInfo = extendedWindowData.relativeWallInfo;
  const wd = globalStores.getDataByIdType(
    relativeWallInfo.relativeWallId,
    relativeWallInfo.wallType || EEntityType.Wall
  );

  const newData = cloneDeep(extendedWindowData);
  if (!wd) return newData;

  let wallStart;
  let wallEnd;
  let wallThickness;
  let wallSeg;

  if (relativeWallInfo.wallType === EEntityType.Wall) {
    const wallData = wd as WallData;
    wallStart = wallData.baseData.start;
    wallEnd = wallData.baseData.end;
    wallThickness = wallData.baseData.thickness;
    wallSeg = isArc(wallData.baseData)
      ? new ArcSeg2d({
          start: wallStart,
          end: wallEnd,
          center: wallData.baseData.center,
          clockwise: wallData.baseData.clockwise
        })
      : new LineSeg2d(wallStart, wallEnd);
  } else {
    const separateWallData = wd as SeparateWallData;
    wallStart = separateWallData.start;
    wallEnd = separateWallData.end;
    wallThickness = separateWallData.thickness;
    wallSeg = isArc(separateWallData)
      ? new ArcSeg2d({
          start: wallStart,
          end: wallEnd,
          center: separateWallData.center,
          clockwise: separateWallData.clockwise
        })
      : new LineSeg2d(wallStart, wallEnd);
  }

  const center = getWindowCenter(wallSeg, newData.distance);
  const rotation = getWindowRotation(wallSeg, center, newData.side === EDoorWindowSide.right);
  const thickness = wallThickness;

  return { ...newData, center, rotation, thickness };
}
